const getObjectBySlug = (associatedObjects = [], slug) =>
  associatedObjects.find(a => a.object_type?.slug === slug)?.object;

const normalizeRatings = (ratings) => {
  if (!ratings) return [];

  if (Array.isArray(ratings)) {
    return ratings;
  }

  return Object.entries(ratings).map(([uuid, rating]) => ({
    uuid,
    rating
  }));
};

/* -------------------------------------------------------------------- */
/* Lookup map builders — turn the Redux-provided reference data into    */
/* flat { uuid: label } maps. Nothing here is hardcoded; every label    */
/* comes from whatever the BE returned in these three collections.      */
/* -------------------------------------------------------------------- */

// generalSkillAttributes: [{ uuid, label, type, placeholder }, ...]
export const buildGeneralSkillsMap = (generalSkillAttributes = []) => {
  const map = {};

  generalSkillAttributes.forEach(attr => {
    if (attr?.uuid && attr?.label) {
      map[attr.uuid] = attr.label;
    }
  });

  return map;
};

// skillCategorized: an ARRAY of single-key objects, one per skill —
// [{ "Bartender - Full Bar": "2c102545-..." }, { "Coffee Designs": "f35b573d-..." }, ...]
// (label -> uuid per entry, as built at login) — flatten + invert to uuid -> label
export const buildSkillCategorizedMap = (skillCategorized = []) => {
  const map = {};

  if (!Array.isArray(skillCategorized)) return map;

  skillCategorized.forEach(entry => {
    if (!entry || typeof entry !== "object") return;

    Object.entries(entry).forEach(([label, uuid]) => {
      if (uuid) {
        map[uuid] = label;
      }
    });
  });

  return map;
};

// eventRoles: reference data for event roles, grouped by category.
// Each category has its own name/uuid plus a nested list of individual
// roles (Actor/Actress, Barback, Security Officer...). Ratings can be
// keyed by either a category uuid or an individual role uuid, so both
// are mapped. Handles eventRoles being either an array of categories
// OR an object keyed by category slug/name — whichever shape Redux
// actually stores, without assuming.
export const buildEventRolesMap = (eventRoles) => {
  const map = {};

  const addEntry = (obj) => {
    const uuid = obj?.uuid;
    const name = obj?.name || obj?.label;

    if (uuid && name) {
      map[uuid] = name;
    }
  };

  const addCategory = (category) => {
    if (!category || typeof category !== "object") return;

    addEntry(category);
    addEntry(category?.event_role_category);

    const roles =
      category?.roles ||
      category?.event_roles ||
      category?.children ||
      [];

    if (Array.isArray(roles)) {
      roles.forEach(addEntry);
    }
  };

  if (Array.isArray(eventRoles)) {
    eventRoles.forEach(addCategory);
  } else if (eventRoles && typeof eventRoles === "object") {
    Object.values(eventRoles).forEach(addCategory);
  }

  return map;
};

/**
 * Resolves a ratings section (general_skills / event_roles / skills)
 * entirely against the uuid -> label map built from Redux reference data.
 * No text parsing, no hardcoded labels — if a uuid isn't in the map,
 * it's rendered as "—" (still filtered out by ExpandedFeedback) so a
 * missing/stale lookup entry never crashes the UI, it just doesn't show.
 */
const buildSection = (statsSource, labelMap = {}) => {
  const statsArr = normalizeRatings(statsSource).filter(
    item => item.rating !== null && item.rating !== undefined
  );

  return statsArr.map(item => ({
    uuid: item.uuid,
    rating: item.rating,
    label: labelMap[item.uuid] || "—"
  }));
};

export const transformFeedback = (
  raw,
  currentUserUuid,
  lookups = {}
) => {
  const {
    generalSkillsMap = {},
    skillsMap = {},
    eventRolesMap = {}
  } = lookups;

  const eventObj = getObjectBySlug(raw.associated_objects, "event");

  // Ratings now come under stats_obj (previously stats)
  const stats = raw.stats_obj || raw.stats || {};

  const generalSkills = buildSection(
    stats.ratings?.general_skills,
    generalSkillsMap
  );
  const eventRoles = buildSection(
    stats.ratings?.event_roles,
    eventRolesMap
  );
  const skills = buildSection(
    stats.ratings?.skills,
    skillsMap
  );

  const allRatings = [...generalSkills, ...eventRoles, ...skills];
  const validRatings = allRatings.filter(
    r => typeof r.rating === "number" && !Number.isNaN(r.rating)
  );
  const computedFallback = validRatings.length
    ? validRatings.reduce((sum, r) => sum + r.rating, 0) / validRatings.length
    : 0;

  const overallRating =
    stats.system_derived_rating ??
    stats.ratings?.system_derived_rating ??
    raw?.system_derived_rating ??
    computedFallback;

  const formatEventDate = (isoDate) => {
    if (!isoDate) return "--";

    const date = new Date(isoDate);
    if (isNaN(date)) return "--";

    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };

  const primaryRole = eventRoles.find(r => r.label !== "—") || skills.find(r => r.label !== "—");

  const permissions = raw.permissions || {};
  const isOwner = raw.created_by?.person_uuid === currentUserUuid;

  return {
    note_id: raw.uuid,
    event: eventObj
      ? { label: eventObj.name || eventObj.display_name }
      : null,
    eventDate: formatEventDate(eventObj?.local_start_at),
    role: primaryRole?.label || null,
    rating: overallRating,
    author: raw.created_by
      ? `${raw.created_by.first_name} ${raw.created_by.last_name}`.trim()
      : "--",
    created_at: formatEventDate(raw.created_at),
    canView: permissions.view ?? true,
    canList: permissions.list ?? true,
    canCreate: permissions.create ?? isOwner,
    canEdit: permissions.edit ?? isOwner,
    canDelete: permissions.delete ?? isOwner,
    desc: raw.desc,
    ratings: {
      general_skills: generalSkills,
      event_roles: eventRoles,
      skills: skills
    }
  };
};

export const transformFeedbacks = (rawList = [], currentUserUuid, lookups = {}) =>
  rawList.map(f => transformFeedback(f, currentUserUuid, lookups));
